import config from '../config';

export const getLocaleFromPath = (path) => {
  const match = path.match(/^\/([a-z]{2})\//);
  return (match && match[1]) || config.locale;
};

/**
 * Normalises a language tag to a `config.languages` key.
 *
 * `navigator.language` yields BCP-47 tags ('fr-FR', 'de-CH'), while
 * `config.languages` is keyed by the short form ('fr', 'de'), so an unnormalised
 * lookup misses for every browser that reports a region.
 *
 * @param {string} [tag] e.g. 'fr-FR'
 * @returns {string|null} e.g. 'fr'
 */
const toLocaleKey = (tag) =>
  typeof tag === 'string' && tag ? tag.split(/[-_]/)[0].toLowerCase() : null;

const isSupported = (key) => !!(key && config.languages[key]);

/**
 * Resolves the locale that the date libraries (moment/dayjs) must run in.
 *
 * The URL prefix is the source of truth for the UI language — it is what
 * LocaleLayout feeds to IntlProvider — so it has to win here too, otherwise
 * /fr renders French copy with German month names. `navigator.language` is only
 * a fallback for the brief moment before the locale-redirect loader has put a
 * prefix on the URL.
 *
 * Client and server MUST agree for the same route or moment/dayjs formatting
 * mismatches on hydration. They do: the server passes `serverContext.locale`
 * (the first path segment, see server/index.js) and the client passes the
 * pathname whose first segment is that same value.
 *
 * @param {Object} [params]
 * @param {string} [params.pathname] a pathname ('/fr/l/slug/123') or a bare
 *   locale ('fr') — the first path segment is what counts either way
 * @param {string} [params.navigatorLanguage] `navigator.language`, if available
 * @returns {string} a key of `config.languages`
 */
export const resolveAppLocale = ({ pathname, navigatorLanguage } = {}) => {
  const pathLocale = toLocaleKey(
    typeof pathname === 'string' ? pathname.split('/').filter(Boolean)[0] : null
  );
  if (isSupported(pathLocale)) return pathLocale;

  const browserLocale = toLocaleKey(navigatorLanguage);
  if (isSupported(browserLocale)) return browserLocale;

  return config.locale;
};
